import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { migrateLocalStorageKeys } from './utils/storageMigration';
import { startAutoFlush } from './services/telemetry';
import App from './App.tsx';
import './index.css';
import './lib/motion/easing.css';

// Migrate aesthetix_* localStorage keys to helios_* (one-time, idempotent)
migrateLocalStorageKeys();

// Drain any queued workout_events left over from a previous offline session.
startAutoFlush();

// Sentry error tracking — catches JS errors in production
// Create a free project at sentry.io and paste your DSN below
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    // Only send errors in production, not during local dev
    enabled: import.meta.env.PROD,
    // Sample 100% of errors (adjust down if you get noisy)
    sampleRate: 1.0,
    // Performance tracing off for now (saves quota)
    tracesSampleRate: 0,
  });
}

// Native chrome setup: dark status bar + dismiss splash once React mounts.
if (Capacitor.isNativePlatform()) {
  StatusBar.setStyle({ style: Style.Dark }).catch(() => { /* unsupported */ });
  // Give React one frame to paint before hiding the splash.
  requestAnimationFrame(() => {
    SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => { /* already hidden */ });
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
